import React, { useEffect, useState } from "react";
import { useBlocker } from "@tanstack/react-router";
import { Modal } from "./ui/modal/modal";
import { Button } from "./ui/button";

export const useUnsavedChangesAlert = (isDirty: () => boolean) => {
  const [exitUrl, setExitUrl] = useState<string | undefined>();
  const [showExitAlert, setShowExitAlert] = useState(false);

  const blocker = useBlocker({
    withResolver: true,
    enableBeforeUnload: true,
    shouldBlockFn: async ({ next }) => {
      if (!(await isDirty()) || showExitAlert) {
        return false;
      }
      setExitUrl(next.fullPath);
      setShowExitAlert(true);
      return true;
    },
  });

  useEffect(() => {
    if (!showExitAlert && blocker && "function" === typeof blocker.reset) {
      blocker.reset();
    }
  }, [showExitAlert, blocker]);

  const onDiscard = () => {
    setShowExitAlert(false);
    if (blocker && "function" === typeof blocker.proceed) {
      blocker.proceed();
    }
  };

  return { showExitAlert, exitUrl, onDiscard };
};

export const UnsavedChangesAlert = ({
  show,
  onDiscard,
}: {
  onDiscard: () => void | Promise<void>;
  show: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(show);

  useEffect(() => {
    setIsOpen(show);
  }, [show]);

  return (
    <Modal.Root open={isOpen} onOpenChange={setIsOpen}>
      <Modal.Panel>
        <Modal.Title>Discard unsaved changes?</Modal.Title>
        <Modal.Description>
          You made changes to this page that you haven't saved. If you leave,
          your changes will be lost.
        </Modal.Description>
        <footer className="flex items-center justify-end gap-4">
          <Button variant="ghost" onClick={() => setIsOpen(false)}>
            Go Back
          </Button>
          <Button variant="destructive" onClick={onDiscard}>
            Discard Changes
          </Button>
        </footer>
      </Modal.Panel>
    </Modal.Root>
  );
};
